import {Component, OnInit} from '@angular/core';
import * as moment from 'moment';
import {AdminStatDataService, AdminUrlService} from '../../services';
import _ from 'lodash';
import {AdminDashboardStatsDTO} from '../../../../interfaces/DTOs/Admin/admin-dashboard-statsDTO.interface';
import {LoggerService} from '../../../../global/services';
import {SimpleChartDataDTO} from '../../../../global/interfaces';

interface IChartData {
  datasets: any;
  labels: any;
  colors: any;
}

interface ISalesCustomerTableData {
  customerName: string;
  totalSales: number;
  monthlyData: { month: string; sales: number }[];
}

@Component({
  selector: 'app-admin-kpis',
  templateUrl: './admin-kpis.component.html',
  styleUrls: ['./admin-kpis.component.scss']
})
export class AdminKpisComponent implements OnInit {
  constructor(private adminDataService: AdminStatDataService,
              private logService: LoggerService,
              public adminUrl: AdminUrlService) {
  }

  logger = this.logService.taggedLogger(this.constructor?.name);

  public chartColorsTotalSales: Array<any> = [
    {
      backgroundColor: 'rgba(255, 99, 132, .7)',
      borderColor: 'rgba(51, 51, 51, 0.5)',
      borderWidth: 1
    }
  ];
  public chartColorsListings: Array<any> = [
    {
      backgroundColor: 'rgba(54, 162, 235, .7)',
      borderColor: 'rgba(51, 51, 51, 0.5)',
      borderWidth: 1
    }
  ];
  public chartColorsVendorSignups: Array<any> = [
    {
      backgroundColor: 'rgba(75, 192, 192, .7)',
      borderColor: 'rgba(51, 51, 51, 0.5)',
      borderWidth: 1
    }
  ];
  public chartColorsBuyerSignups: Array<any> = [
    {
      backgroundColor: 'rgba(153, 102, 255, .7)',
      borderColor: 'rgba(51, 51, 51, 0.5)',
      borderWidth: 1
    }
  ];

  public totalSalesData: IChartData;
  public monthlyListingTotalsData: IChartData;
  public monthlyVendorSignupsData: IChartData;
  public monthlyBuyerSignupsData: IChartData;
  public salesByCustomerTableData: ISalesCustomerTableData[] = [];
  public adminStats: AdminDashboardStatsDTO;

  async ngOnInit() {
    this.loadData();
  }

  loadData() {
    // Load total sales by month (no contactId - always null)
    this.adminDataService.getTotalSalesByMonth().then(result => {
      this.totalSalesData = this.getSingleChartDataset(this.chartColorsTotalSales, 'Total Sales Monthly', result);
    });

    // Load monthly listing totals
    this.adminDataService.getMonthlyListingTotals().then(result => {
      this.monthlyListingTotalsData = this.getSingleChartDataset(this.chartColorsListings, 'Monthly Listing Totals', result);
    });

    // Load monthly vendor signups
    this.adminDataService.getMonthlyVendorSignups().then(result => {
      this.monthlyVendorSignupsData = this.getSingleChartDataset(this.chartColorsVendorSignups, 'Monthly Vendor Signups', result);
    });

    // Load monthly buyer signups
    this.adminDataService.getMonthlyBuyerSignups().then(result => {
      this.monthlyBuyerSignupsData = this.getSingleChartDataset(this.chartColorsBuyerSignups, 'Monthly Buyer Signups', result);
    });

    // Load sales by customer for table display (no contactId - always null)
    this.adminDataService.getSalesByCustomer(null).then(result => {
      this.salesByCustomerTableData = this.processSalesCustomerTableData(result);
      this.logger.info("Sales by customer table data: ", this.salesByCustomerTableData);
    });

    // Load admin stats (no contactId - always null)
    this.adminDataService.getAdminStats(null).then(result => {
      this.adminStats = result;
    });
  }

  getSingleChartDataset(cols, dataLabels, data: SimpleChartDataDTO[]): IChartData {
    const labels: Array<any> = [];
    const datapoints = [];
    const datas: Array<any> = [];

    data.forEach(d => {
      if (d.date) {
        labels.push(moment(d.date).format("MMM YY"));
      } else {
        labels.push(d.label);
      }
      datapoints.push(d.total);
    });

    datas.push({data: datapoints, label: dataLabels, lineTension: 0.1});
    return {datasets: datas, labels, colors: cols};
  }

  processSalesCustomerTableData(data: SimpleChartDataDTO[]): ISalesCustomerTableData[] {
    // Group data by customer (extraData contains customer name)
    const customerGroups = _.groupBy(data, 'extraData');

    return Object.keys(customerGroups).map(customerName => {
      const customerData = customerGroups[customerName];
      const totalSales = _.sumBy(customerData, 'total');

      const monthlyData = customerData.map(item => ({
        month: item.date ? moment(item.date).format("MMM YY") : item.label,
        sales: item.total
      })).sort((a, b) => moment(a.month, "MMM YY").valueOf() - moment(b.month, "MMM YY").valueOf());

      return {
        customerName,
        totalSales,
        monthlyData
      };
    }).sort((a, b) => b.totalSales - a.totalSales); // Sort by total sales descending
  }
}